'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { toBigInt, useCachedValue } from '@morpho-org/dapp-core';
import { formatLongString, isDefined, type Time } from '@morpho-org/morpho-ts';
import { type OptionSelect, Select } from '@morpho-org/ui-v6/client';
import { Card, CardText, Skeleton, TableNoResults } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { SegmentedControlItem, SegmentedControlList, Tabs, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { formatUnits } from 'viem';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { formatTimeseriesData, isSerieEmpty, removeNullYValuesFromSeries } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { getTimeFrameOptionsForVaultAge, vaultV2DefaultTimeFrame } from '@/config';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getParsedValue } from '@/helpers/formatters';
import { splitValueAndUnit } from '@/helpers/number';
import { ceilOfHour, getHistoryRangeFromTimeFrame, isUnpricedPositionHistory } from '@/helpers/timeseries';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { orpc } from '@/lib/orpc/client';
import { errorCodeOf } from '@/lib/orpc/errors';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { getAssetAmountDigits } from './assetAmountDigits';
import { canSwitchPositionMode, getHeadlineDecimals, isHeadlineUnavailable, isPositionSeriesUnavailable, type PositionChartMode, showPositionEmptyState } from './positionChartState';
export interface VaultV2YourPositionChartClientProps {
  vaultAddress: Address;
  chainId: ChainId;
}
type Mode = PositionChartMode;
const formatter = formatters.short.trailingZero(false).default('0.00');
const VaultV2YourPositionChartClient = t0 => {
  const $ = _c(85);
  const {
    vaultAddress,
    chainId
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  const isMobile = useIsMobile();
  const {
    account
  } = useWeb3Context();
  const {
    positionHistory: isPositionHistoryEnabled
  } = useFeatureFlags();
  const now = ceilOfHour();
  const [timeFrame, setTimeFrame] = useState(vaultV2DefaultTimeFrame);
  const [mode, setMode] = useState("assets");
  const isEmpty = !account.address;
  let range;
  let t1;
  let t2;
  if ($[0] !== account.address || $[1] !== chainId || $[2] !== timeFrame || $[3] !== vaultAddress) {
    range = getHistoryRangeFromTimeFrame(timeFrame);
    t1 = useQuery;
    let t3;
    if ($[7] !== account.address || $[8] !== chainId || $[9] !== vaultAddress) {
      t3 = (previousData, previousQuery) => {
        const previous = previousQuery?.queryKey[1] as {
          input?: {
            chainId?: number;
            address?: Address;
            userAddress?: Address;
          };
        } | undefined;
        const sameWallet = isDefined(account.address) && previous?.input?.userAddress === account.address && previous.input.address === vaultAddress && previous.input.chainId === chainId;
        return sameWallet ? previousData : undefined;
      };
      $[7] = account.address;
      $[8] = chainId;
      $[9] = vaultAddress;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = orpc.vaultV2.userPositionHistory.queryOptions({
      input: account.address ? {
        chainId,
        address: vaultAddress,
        userAddress: account.address,
        range
      } : skipToken,
      placeholderData: t3
    });
    $[0] = account.address;
    $[1] = chainId;
    $[2] = timeFrame;
    $[3] = vaultAddress;
    $[4] = range;
    $[5] = t1;
    $[6] = t2;
  } else {
    range = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const {
    data,
    isLoading: rangeLoading,
    isPlaceholderData,
    error
  } = t1(t2);
  const timeFrameOptions = getTimeFrameOptionsForVaultAge(data?.creationTimestamp, now);
  const userAddress = account.address;
  let t3;
  if ($[11] !== chainId || $[12] !== userAddress || $[13] !== vaultAddress) {
    t3 = orpc.vaultV2.userPosition.queryOptions({
      input: userAddress ? {
        chainId,
        address: vaultAddress,
        userAddress
      } : skipToken
    });
    $[11] = chainId;
    $[12] = userAddress;
    $[13] = vaultAddress;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const {
    data: userPositionState,
    isLoadingError: snapshotFailed
  } = useQuery(t3);
  const asset = data?.asset;
  const points = data?.points ?? [];
  const decimals = asset?.decimals ?? null;
  let options;
  if ($[15] !== asset?.symbol || $[16] !== data) {
    options = [];
    if (data !== undefined) {
      const t4 = asset?.symbol ?? "Asset";
      let t5;
      if ($[18] !== t4) {
        t5 = {
          value: "assets",
          label: t4
        };
        $[18] = t4;
        $[19] = t5;
      } else {
        t5 = $[19];
      }
      options.push(t5);
    }
    let t4;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        value: "assetsUsd",
        label: "USD"
      };
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    options.push(t4);
    $[15] = asset?.symbol;
    $[16] = data;
    $[17] = options;
  } else {
    options = $[17];
  }
  const modeOptions = options;
  let t4;
  if ($[21] !== error) {
    t4 = errorCodeOf(error);
    $[21] = error;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const isNotFound = t4 === "NOT_FOUND";
  const chartError = !isNotFound && error !== null;
  let _data = [];
  bb0: switch (mode) {
    case "assets":
      {
        _data = decimals == null ? [] : formatTimeseriesData(points.map(point_0 => ({
          x: point_0.timestamp,
          y: Number(formatUnits(BigInt(point_0.assets), decimals))
        })));
        break bb0;
      }
    case "assetsUsd":
      {
        _data = formatTimeseriesData(points.map(_temp));
      }
  }
  const formattedData = removeNullYValuesFromSeries(_data);
  const hasData = !isSerieEmpty(formattedData);
  const hasPosition = points.length > 0;
  const seriesUnavailable = isPositionSeriesUnavailable(points.length, formattedData) || mode === "assetsUsd" && isUnpricedPositionHistory(points);
  const hasSnapshotBalance = isDefined(userPositionState?.position);
  const isRangeEmpty = data !== undefined && !isPlaceholderData && !hasPosition;
  const needsAllTimeHistory = isRangeEmpty && range !== "all" && userPositionState !== undefined && !hasSnapshotBalance;
  let t5;
  if ($[23] !== account.address || $[24] !== chainId || $[25] !== needsAllTimeHistory || $[26] !== vaultAddress) {
    t5 = orpc.vaultV2.userPositionHistory.queryOptions({
      input: needsAllTimeHistory && account.address ? {
        chainId,
        address: vaultAddress,
        userAddress: account.address,
        range: "all"
      } : skipToken
    });
    $[23] = account.address;
    $[24] = chainId;
    $[25] = needsAllTimeHistory;
    $[26] = vaultAddress;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const {
    data: allTimeHistory,
    isLoading: allTimeLoading
  } = useQuery(t5);
  const loading = rangeLoading || allTimeLoading;
  const stateInput = {
    rangePointCount: points.length,
    allTimePointCount: allTimeHistory?.points.length,
    hasSnapshotBalance
  };
  const canSwitchMode = canSwitchPositionMode(stateInput);
  const showEmptyState = showPositionEmptyState({
    ...stateInput,
    isPositionHistoryEnabled,
    loading,
    chartError,
    isNotFound,
    isRangeEmpty,
    isAllTimeRange: range === "all",
    snapshotLoaded: userPositionState !== undefined,
    hasData
  });
  const position = userPositionState?.position;
  const assets = useCachedValue(position === null ? "0" : position?.assets);
  const assetsUsd = useCachedValue(position === null ? 0 : position?.assetsUsd);
  let formattedTotal;
  let t6;
  if ($[28] !== asset?.decimals || $[29] !== asset?.priceUsd || $[30] !== asset?.symbol || $[31] !== assets || $[32] !== assetsUsd || $[33] !== isEmpty || $[34] !== mode || $[35] !== position || $[36] !== snapshotFailed) {
    const total = {
      value: mode === "assetsUsd" ? getParsedValue(assetsUsd, safeParseNumber) : assets,
      decimals: getHeadlineDecimals(mode, asset?.decimals) ?? (isEmpty ? 18 : null)
    };
    const headlineUnavailable = isHeadlineUnavailable({
      mode,
      isConnected: !isEmpty,
      snapshotFailed,
      position
    });
    let t7;
    bb1: {
      if (total.decimals === null || headlineUnavailable) {
        let t8;
        if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = {
            value: "\u2014",
            unit: ""
          };
          $[39] = t8;
        } else {
          t8 = $[39];
        }
        t7 = t8;
        break bb1;
      }
      const formattedValue = formatter.digits(mode === "assetsUsd" ? 2 : getAssetAmountDigits(asset?.priceUsd, toBigInt(total.value) ?? null, asset?.decimals)).trailingZero(!total.value).of(toBigInt(total.value), total.decimals);
      t7 = splitValueAndUnit(formattedValue);
    }
    formattedTotal = t7;
    bb2: {
      if (total.decimals === null || headlineUnavailable) {
        t6 = null;
        break bb2;
      }
      const unit = mode === "assetsUsd" ? "$" : asset?.symbol;
      t6 = formatters.commas.unit(unit).digits(mode === "assetsUsd" ? 2 : getAssetAmountDigits(asset?.priceUsd, toBigInt(total.value) ?? null, asset?.decimals)).of(toBigInt(total.value), total.decimals);
    }
    $[28] = asset?.decimals;
    $[29] = asset?.priceUsd;
    $[30] = asset?.symbol;
    $[31] = assets;
    $[32] = assetsUsd;
    $[33] = isEmpty;
    $[34] = mode;
    $[35] = position;
    $[36] = snapshotFailed;
    $[37] = formattedTotal;
    $[38] = t6;
  } else {
    formattedTotal = $[37];
    t6 = $[38];
  }
  const detailedFormattedTotal = t6;
  let t7;
  bb3: {
    const label = modeOptions.find(option => option.value === mode)?.label;
    if (label) {
      t7 = `${"My Deposit"} (${label})`;
      break bb3;
    }
    t7 = "My Deposit";
  }
  const title = t7;
  let t8;
  bb4: switch (mode) {
    case "assetsUsd":
      {
        t8 = "$";
        break bb4;
      }
    case "assets":
    default:
      {
        const t9 = asset?.symbol ?? "";
        let t10;
        if ($[40] !== t9) {
          t10 = formatLongString(t9);
          $[40] = t9;
          $[41] = t10;
        } else {
          t10 = $[41];
        }
        t8 = t10;
      }
  }
  const unit_0 = t8;
  if (showEmptyState) {
    let t9;
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <TableNoResults text="No position found." height="394px" withBorder={true} className="rounded-[20px]" />;
      $[42] = t9;
    } else {
      t9 = $[42];
    }
    return t9;
  }
  let t9;
  if ($[43] !== title) {
    t9 = <span className="text-secondary-foreground text-xs">{title}</span>;
    $[43] = title;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== detailedFormattedTotal || $[46] !== formattedTotal || $[47] !== loading || $[48] !== mode) {
    t10 = loading ? <div className="gap-sm flex items-center"><Skeleton className="h-(--text-title-sm--line-height) w-[130px]" /></div> : <div className="gap-sm flex items-center"><Tooltip><TooltipTrigger asChild={true}><div className="flex cursor-default items-center gap-[2px]">{mode === "assetsUsd" && <span className="text-title-sm text-muted-foreground">$</span>}<span className="text-title-sm">{formattedTotal.value}</span><span className="text-title-sm text-muted-foreground">{formattedTotal.unit}</span></div></TooltipTrigger>{!!detailedFormattedTotal && <TooltipContent side="right">{detailedFormattedTotal}</TooltipContent>}</Tooltip></div>;
    $[45] = detailedFormattedTotal;
    $[46] = formattedTotal;
    $[47] = loading;
    $[48] = mode;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] !== t10 || $[51] !== t9) {
    t11 = <div className="gap-xs flex flex-col">{t9}{t10}</div>;
    $[50] = t10;
    $[51] = t9;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== canSwitchMode || $[54] !== isEmpty || $[55] !== isMobile || $[56] !== loading || $[57] !== mode || $[58] !== modeOptions || $[59] !== setMode) {
    t12 = isMobile ? <Select options={modeOptions} value={mode} onChange={setMode} disabled={!canSwitchMode || loading} /> : !isEmpty && <Tabs value={mode} onValueChange={value => setMode(value as Mode)}><SegmentedControlList size="default" aria-label="Currency">{modeOptions.map(option_0 => <SegmentedControlItem key={option_0.value} value={option_0.value} disabled={!canSwitchMode || loading}>{option_0.label}</SegmentedControlItem>)}</SegmentedControlList></Tabs>;
    $[53] = canSwitchMode;
    $[54] = isEmpty;
    $[55] = isMobile;
    $[56] = loading;
    $[57] = mode;
    $[58] = modeOptions;
    $[59] = setMode;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  const t13 = isEmpty || !isPositionHistoryEnabled;
  let t14;
  if ($[61] !== setTimeFrame || $[62] !== t13 || $[63] !== timeFrame || $[64] !== timeFrameOptions) {
    t14 = <Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} disabled={t13} />;
    $[61] = setTimeFrame;
    $[62] = t13;
    $[63] = timeFrame;
    $[64] = timeFrameOptions;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  let t15;
  if ($[66] !== t12 || $[67] !== t14) {
    t15 = <div className="gap-sm flex flex-wrap items-center">{t12}{t14}</div>;
    $[66] = t12;
    $[67] = t14;
    $[68] = t15;
  } else {
    t15 = $[68];
  }
  let t16;
  if ($[69] !== t11 || $[70] !== t15) {
    t16 = <div className="gap-sm flex flex-wrap items-center justify-between">{t11}{t15}</div>;
    $[69] = t11;
    $[70] = t15;
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  let t17;
  if ($[72] !== chartError || $[73] !== formattedData || $[74] !== hasData || $[75] !== isPositionHistoryEnabled || $[76] !== loading || $[77] !== mode || $[78] !== palette || $[79] !== seriesUnavailable || $[80] !== unit_0) {
    t17 = !isPositionHistoryEnabled ? <CardText text="Position history is temporarily unavailable." height="224px" /> : <SimpleChart statuses={{
      loading,
      error: chartError || seriesUnavailable
    }} unit={unit_0} height="224px" series={hasData ? [{
      type: "areaspline",
      name: `Total ${mode}`,
      data: formattedData,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }] : []} additionalOptions={{
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    }} />;
    $[72] = chartError;
    $[73] = formattedData;
    $[74] = hasData;
    $[75] = isPositionHistoryEnabled;
    $[76] = loading;
    $[77] = mode;
    $[78] = palette;
    $[79] = seriesUnavailable;
    $[80] = unit_0;
    $[81] = t17;
  } else {
    t17 = $[81];
  }
  let t18;
  if ($[82] !== t16 || $[83] !== t17) {
    t18 = <Card className="gap-lg py-lg px-md md:px-lg w-full flex-col" withBorder={true} data-testid="vault-v2-position-chart">{t16}{t17}</Card>;
    $[82] = t16;
    $[83] = t17;
    $[84] = t18;
  } else {
    t18 = $[84];
  }
  return t18;
};
export default VaultV2YourPositionChartClient;
function _temp(point) {
  return {
    x: point.timestamp,
    y: point.assetsUsd
  };
}